import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

import { ADMIN_EMAILS } from "../lib/adminEmails";
const BALANCE_REFRESH_EVENT = "echonour:balance-refresh";

interface BalanceResponse {
  minutes_remaining: number;
  free_minutes_used: number;
  purchased_minutes_used: number;
}

export default function BalanceDisplay() {
  const { user, balanceVersion } = useAuth();
  const [minutes, setMinutes] = useState<number | null>(null);

  const isAdmin = !!user && ADMIN_EMAILS.includes(user.email);

  const refreshBalance = useCallback(() => {
    if (!user || isAdmin) return;

    apiFetch<BalanceResponse>("/billing/balance")
      .then((data) => setMinutes(data.minutes_remaining))
      .catch(() => setMinutes(null));
  }, [user, isAdmin, balanceVersion]);

  useEffect(() => {
    refreshBalance();
  }, [refreshBalance]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refreshBalance();
    };

    window.addEventListener(BALANCE_REFRESH_EVENT, refreshBalance);
    window.addEventListener("focus", refreshBalance);
    window.addEventListener("storage", refreshBalance);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      window.removeEventListener(BALANCE_REFRESH_EVENT, refreshBalance);
      window.removeEventListener("focus", refreshBalance);
      window.removeEventListener("storage", refreshBalance);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [refreshBalance]);

  if (!user) return null;

  if (isAdmin) {
    return (
      <span className="flex items-center gap-1.5 rounded-md bg-white/10 px-2.5 py-1 text-sm font-medium text-white/80">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-4 w-4"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 000-1.5h-3.25V5z"
            clipRule="evenodd"
          />
        </svg>
        &#8734;
      </span>
    );
  }

  if (minutes === null) return null;

  const low = minutes < 10;

  return (
    <Link
      to="/pricing"
      className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm font-medium transition ${
        low
          ? "bg-red-500/20 text-red-200 hover:bg-red-500/30"
          : "bg-white/10 text-white/80 hover:bg-white/20"
      }`}
      title="Remaining transcription minutes"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="h-4 w-4"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 000-1.5h-3.25V5z"
          clipRule="evenodd"
        />
      </svg>
      {minutes} min
    </Link>
  );
}
